import { useRef } from 'react';
import { UploadSimple } from '@phosphor-icons/react';

/**
 * A labelled HTML field the instructor can either paste into or populate by
 * uploading an .html file (its text is read in and stored — no blob upload).
 */
export function HtmlUploadField({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    onChange(await file.text());
  }

  return (
    <label className="block">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="label-mono block">{label}</span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-brand-teal hover:underline"
        >
          <UploadSimple size={13} weight="bold" /> Upload .html
        </button>
        <input ref={inputRef} type="file" accept=".html,.htm,text/html" className="hidden" onChange={onFile} />
      </div>
      <textarea
        className="input font-mono text-xs"
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <div className="mt-1 flex items-center justify-between gap-2">
        {hint && <p className="text-xs text-slate-400">{hint}</p>}
        {value.trim() && (
          <p className="ml-auto shrink-0 text-xs font-medium text-emerald-600">
            ✓ {value.length.toLocaleString()} characters
          </p>
        )}
      </div>
    </label>
  );
}
